import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { AlertTriangle, X as XIcon, RefreshCw } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import SubscriptionSection from '../components/settings/SubscriptionSection';

export default function SettingsBillingPage() {
  const { t, i18n } = useTranslation();
  const { user, practice } = useAuth();
  const [groupBilling, setGroupBilling] = useState(null);
  const [showCancel, setShowCancel] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [unCancelling, setUnCancelling] = useState(false);

  const load = React.useCallback(async () => {
    try { const r = await api.get('/api/stripe/group-billing'); setGroupBilling(r.data); }
    catch (_) { /* swallow */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const locale = i18n.language?.startsWith('en') ? 'en-GB' : 'de-DE';
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

  async function confirmCancel() {
    setCancelling(true);
    try {
      const r = await api.post('/api/stripe/cancel', {});
      toast.success(t('settings.billing.cancelScheduled', { date: fmtDate(r.data.scheduled_cancel_at) }));
      setShowCancel(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || t('errors.unknown'));
    } finally { setCancelling(false); }
  }
  async function reverseCancel() {
    setUnCancelling(true);
    try {
      await api.post('/api/stripe/uncancel', {});
      toast.success(t('settings.billing.uncancelled'));
      load();
    } catch (e) { toast.error(e.response?.data?.message || t('errors.unknown')); }
    finally { setUnCancelling(false); }
  }

  if (!practice || !groupBilling) return null;

  // Branch admin — read-only view.
  if (!groupBilling.is_main_location) {
    return (
      <div className="space-y-5" data-testid="billing-branch-view">
        <div className="rounded-md border border-accent/30 bg-accent/5 p-4 text-sm" data-testid="billing-managed-by-main">
          {t('settings.billing.managedByMain', { name: groupBilling.main_practice_name })}
        </div>
        <div className="so-card p-5">
          <h2 className="text-sm font-semibold mb-3">{t('settings.billing.ownUsageTitle')}</h2>
          <p className="text-sm text-text-secondary">{t('settings.billing.ownUsageMessages', { count: groupBilling.own_usage.messages_this_month })}</p>
          <p className="text-sm text-text-secondary">{t('settings.billing.ownUsageSeats', { count: groupBilling.own_usage.staff })}</p>
          <p className="text-xs text-text-secondary mt-3">{t('settings.billing.plan')}: <strong>{groupBilling.plan}</strong> ({groupBilling.billing_cycle})</p>
        </div>
      </div>
    );
  }

  // Hauptstandort — full billing view.
  const isCancelScheduled = !!groupBilling.cancel_at_period_end;
  return (
    <div className="space-y-6" data-testid="billing-main-view">
      {isCancelScheduled && (
        <div className="rounded-md border border-warning/40 bg-warning/10 p-4 flex items-start gap-3" data-testid="billing-cancel-banner">
          <AlertTriangle size={18} className="text-warning shrink-0 mt-0.5"/>
          <div className="flex-1">
            <p className="text-sm font-semibold text-warning">{t('settings.billing.cancelBannerTitle')}</p>
            <p className="text-xs text-text-secondary mt-1">
              {t('settings.billing.cancelBannerBody', { date: fmtDate(groupBilling.scheduled_cancel_at) })}
            </p>
            {groupBilling.scheduled_delete_at && (
              <p className="text-xs text-text-secondary mt-1">{t('settings.billing.deleteScheduled', { date: fmtDate(groupBilling.scheduled_delete_at) })}</p>
            )}
            <button onClick={reverseCancel} disabled={unCancelling} className="so-btn so-btn-secondary text-xs mt-2" data-testid="billing-uncancel-btn">
              <RefreshCw size={12}/>{unCancelling ? '…' : t('settings.billing.uncancelBtn')}
            </button>
          </div>
        </div>
      )}

      <SubscriptionSection practice={practice}/>

      {!isCancelScheduled && (
        <section className="so-card p-5 border-danger/30" data-testid="cancel-section">
          <h2 className="text-sm font-semibold text-danger mb-2">{t('settings.billing.cancelSectionTitle')}</h2>
          <p className="text-xs text-text-secondary mb-4">{t('settings.billing.cancelSectionHint')}</p>
          <button onClick={() => setShowCancel(true)} className="so-btn text-sm border border-danger text-danger hover:bg-danger/5" data-testid="billing-cancel-btn">
            {t('settings.billing.cancelBtn')}
          </button>
        </section>
      )}

      {showCancel && (
        <CancelModal
          billingCycle={practice.billing_cycle || 'monthly'}
          contractEnd={practice.contract_end}
          periodEnd={practice.current_period_end}
          fmtDate={fmtDate}
          onConfirm={confirmCancel}
          onClose={() => setShowCancel(false)}
          submitting={cancelling}
          t={t}
        />
      )}
    </div>
  );
}

function CancelModal({ billingCycle, contractEnd, periodEnd, fmtDate, onConfirm, onClose, submitting, t }) {
  const effective = billingCycle === 'annual' && contractEnd ? contractEnd : periodEnd;
  const cycleNote = billingCycle === 'annual'
    ? t('settings.billing.modal.annualNote', { date: fmtDate(effective) })
    : t('settings.billing.modal.monthlyNote', { date: fmtDate(effective) });

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="cancel-modal">
      <div className="bg-white rounded-md shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-base font-semibold text-danger flex items-center gap-2"><AlertTriangle size={18}/>{t('settings.billing.modal.title')}</h3>
          <button onClick={onClose}><XIcon size={18} className="text-text-secondary"/></button>
        </div>
        <p className="text-sm text-text">{t('settings.billing.modal.body', { date: fmtDate(effective) })}</p>
        <p className="text-sm text-text-secondary mt-2">{cycleNote}</p>
        <div className="mt-5 flex items-center justify-end gap-2">
          <button onClick={onClose} className="so-btn so-btn-secondary text-sm" data-testid="cancel-modal-abort">{t('common.cancel')}</button>
          <button onClick={onConfirm} disabled={submitting} className="so-btn text-sm bg-danger text-white hover:opacity-90" data-testid="cancel-modal-confirm">
            {submitting ? '…' : t('settings.billing.modal.confirmBtn')}
          </button>
        </div>
      </div>
    </div>
  );
}
